import moment, { Moment } from "moment";

import { SeriesID } from "../Types/Common/src/ids";
import { FvcSeries } from "../Types/Common/src/pft";

export const ATS_REVERSIBILITY_PCT = 0.12;
export const ATS_REVERSIBILITY_ABS_L = 0.2;

export const BD_DROP_COLOR = "#b35a00";

type Metric = "fev1" | "fvc";

export interface BdMetricComparison {
  pre: number | undefined;
  post: number | undefined;
  predicted: number | undefined;
  /** Pre series' own % predicted — backend `*PercPred`, passed through (fraction). */
  prePctPred: number | undefined;
  /** Post series' own % predicted — backend `*PercPred`, passed through (fraction). */
  postPctPred: number | undefined;
  absChangeL: number | undefined;
  pctFromBaseline: number | undefined;
  pctFromPredicted: number | undefined;
  positiveResponse: boolean | undefined;
  positiveResponseByBasis: Record<BdDecisionBasis, boolean | undefined>;
  significantDecline: boolean | undefined;
}

export interface BdComparison {
  fev1: BdMetricComparison;
  fvc: BdMetricComparison;
}

export interface BdZScores {
  fvcZ: number | undefined;
  fev1Z: number | undefined;
  fev1FvcZ: number | undefined;
}

/**
 * Prev program-configured reversibility selector. It did drive BOTH
 * which % change column(s) the comparison table shows AND (via `resolveBdBasis`)
 * which % the "Reversibility Met?" verdict is measured against:
 * - `predicted` (default) — show only "% from Predicted"; judge on % predicted.
 * - `baseline`            — show only "% from Baseline";  judge on % from the pre-BD value.
 * - `both`                — show both % columns;          judge on % predicted.
 * Columns are now configured independently via `bdReversibilityPctColumns`;
 * when that field is absent this selector still determines the columns so
 * existing programs render unchanged. Verdict columns can be overridden separately
 * via `bdReversibilityVerdictColumns`.
 */
export type BdReversibilityBasis = "both" | "baseline" | "predicted";

/** A reference used to calculate a reversibility verdict. */
export type BdDecisionBasis = "baseline" | "predicted";

export const BD_VERDICT_BASIS_LABELS: Record<BdDecisionBasis, string> = {
  baseline: "% from Baseline",
  predicted: "% from Predicted",
};

/** Which metric rows the comparison table renders. */
export type BdReversibilityMetrics = "both" | "fev1" | "fvc";

export interface BdThresholds {
  pctThreshold: number;
  /** Absolute-volume floor in liters; `undefined` ⇒ no floor, the % threshold alone decides. */
  absThresholdL?: number;
  /** Absent is treated as `predicted`. */
  basis?: BdReversibilityBasis;
}

export const DEFAULT_BD_THRESHOLDS: BdThresholds = {
  pctThreshold: ATS_REVERSIBILITY_PCT,
  absThresholdL: ATS_REVERSIBILITY_ABS_L,
  basis: "predicted",
};

/** Normalise an arbitrary (possibly absent) selector to a known member; absent/unknown → `predicted`. */
export const resolveBdReversibilityBasis = (basis?: BdReversibilityBasis | string | null): BdReversibilityBasis =>
  basis === "baseline" ? "baseline" : basis === "both" ? "both" : "predicted";

/**
 * The reference the "Reversibility Met?" verdict (and the footnote/criteria) is
 * computed against. `both` — and any unknown value — judge on % predicted.
 */
export const resolveBdBasis = (basis?: BdReversibilityBasis | string | null): BdDecisionBasis =>
  basis === "baseline" ? "baseline" : "predicted";

export const resolveBdColumns = (
  basis?: BdReversibilityBasis | string | null,
): { showBaseline: boolean; showPredicted: boolean } => {
  const b = resolveBdReversibilityBasis(basis);
  if (b === "baseline") return { showBaseline: true, showPredicted: false };
  if (b === "both") return { showBaseline: true, showPredicted: true };
  return { showBaseline: false, showPredicted: true };
};

/** Normalise the metric-rows selector; absent/unknown -> both rows */
export const resolveBdMetrics = (
  metrics?: BdReversibilityMetrics | string | null,
): { fev1: boolean; fvc: boolean } =>
  metrics === "fev1" ? { fev1: true, fvc: false }
    : metrics === "fvc" ? { fev1: false, fvc: true }
      : { fev1: true, fvc: true };

type BdSeries = {
  fev1?: number;
  fvc?: number;
  fev1Pred?: number;
  fvcPred?: number;
  fev1PercPred?: number;
  fvcPercPred?: number;
};

const PRED_DIVERGENCE_TOLERANCE_L = 0.01;

const THRESHOLD_EPSILON = 1e-9;

const isFiniteNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

export const parsePositiveSetting = (v: number | string | null | undefined): number | undefined => {
  if (v === null || v === undefined || (typeof v === "string" && v.trim() === "")) return undefined;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

/**
 * Convert program-configured thresholds (percent as a whole number, volume in mL) into library-native units, falling back to the ATS defaults when unset.
 * `disableVolumeFloor` removes the absolute-mL floor entirely (the % threshold alone decides); absent/false keeps behavior.
 */
export const resolveBdThresholds = (
  pctWhole?: number | string | null,
  volumeMl?: number | string | null,
  basis?: BdReversibilityBasis | string | null,
  disableVolumeFloor?: boolean | null,
): BdThresholds => {
  const pct = parsePositiveSetting(pctWhole);
  const volume = parsePositiveSetting(volumeMl);
  return {
    pctThreshold: pct !== undefined ? pct / 100 : ATS_REVERSIBILITY_PCT,
    absThresholdL: disableVolumeFloor
      ? undefined
      : volume !== undefined ? volume / 1000 : ATS_REVERSIBILITY_ABS_L,
    basis: resolveBdReversibilityBasis(basis),
  };
};

/** The reversibility-display slice of a program's featureSet. Callers can pass `program?.featureSet`. */
export interface BdReversibilityFeatureSet {
  bdReversibilityPctThreshold?: number | string | null;
  bdReversibilityVolumeThresholdMl?: number | string | null;
  bdReversibilityBasis?: BdReversibilityBasis | string | null;
  bdReversibilityPctColumns?: BdReversibilityBasis | string | null;
  bdReversibilityVerdictColumns?: BdReversibilityBasis | string | null;
  bdReversibilityMetrics?: BdReversibilityMetrics | string | null;
  bdHideReversibilityVerdict?: boolean | null;
  bdDisableVolumeFloor?: boolean | null;
}

/** Everything the comparison table (in-page and PDF) needs to render. */
export interface BdDisplaySettings {
  thresholds: BdThresholds;
  columns: { showBaseline: boolean; showPredicted: boolean };
  metrics: { fev1: boolean; fvc: boolean };
  showVerdict: boolean;
  verdictBases: BdDecisionBasis[];
}

/**
 * Resolve a program featureSet into the full reversibility display config.
 * Every field is optional and defaults to today's rendering: predicted-basis verdict shown, columns per the legacy `bdReversibilityBasis` selector, both metric rows, ATS thresholds with the mL floor applied.
 */
export const resolveBdDisplaySettings = (
  featureSet?: BdReversibilityFeatureSet | null,
): BdDisplaySettings => {
  const fs = featureSet ?? {};
  const decisionBasis = resolveBdBasis(fs.bdReversibilityBasis);
  const verdictBases: BdDecisionBasis[] = fs.bdReversibilityVerdictColumns === "both"
    ? ["baseline", "predicted"]
    : [fs.bdReversibilityVerdictColumns === "baseline" || fs.bdReversibilityVerdictColumns === "predicted"
      ? fs.bdReversibilityVerdictColumns
      : decisionBasis];
  // Columns decouple from the verdict basis when configured; the prev coupled selector remains the fallback so existing programs are unchanged.
  const columns = resolveBdColumns(fs.bdReversibilityPctColumns ?? fs.bdReversibilityBasis);
  // Preserve the decision-reference column for existing programs and decline highlighting.
  if (resolveBdBasis(fs.bdReversibilityBasis) === "baseline") columns.showBaseline = true;
  else columns.showPredicted = true;
  if (!fs.bdHideReversibilityVerdict) {
    if (verdictBases.includes("baseline")) columns.showBaseline = true;
    if (verdictBases.includes("predicted")) columns.showPredicted = true;
  }
  return {
    thresholds: resolveBdThresholds(
      fs.bdReversibilityPctThreshold,
      fs.bdReversibilityVolumeThresholdMl,
      fs.bdReversibilityBasis,
      !!fs.bdDisableVolumeFloor,
    ),
    columns,
    metrics: resolveBdMetrics(fs.bdReversibilityMetrics),
    showVerdict: !fs.bdHideReversibilityVerdict,
    verdictBases,
  };
};

export const DEFAULT_BD_DISPLAY_SETTINGS: BdDisplaySettings = resolveBdDisplaySettings();

/**
 * Master on/off for the Pre/Post-BD (reversibility) feature. Gates every BD
 * surface off a single program-level flag: the kiosk post-BD administration
 * screen, the in-page comparison panels, and the Pre/Post BD report. Absent or
 * false ⇒ the feature is hidden and post-BD kiosk sessions launch as normal
 * spirometry. Structural param so callers can pass `program?.featureSet`.
 */
export const isBdFeatureEnabled = (
  featureSet?: { enablePrePostBd?: boolean } | null,
): boolean => !!featureSet?.enablePrePostBd;

function compareMetric(metric: Metric, pre: BdSeries, post: BdSeries, thresholds: BdThresholds): BdMetricComparison {
  const preVal = pre[metric];
  const postVal = post[metric];
  const predKey = metric === "fev1" ? "fev1Pred" : "fvcPred";
  const percPredKey = metric === "fev1" ? "fev1PercPred" : "fvcPercPred";
  const predicted = pre[predKey];

  if (
    process.env.NODE_ENV !== "production"
    && isFiniteNumber(predicted)
    && isFiniteNumber(post[predKey])
    && Math.abs(predicted - (post[predKey] as number)) > PRED_DIVERGENCE_TOLERANCE_L
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[bdLib] ${predKey} differs between pre (${predicted}) and post (${post[predKey]}). Using pre as predicted reference.`
    );
  }

  // Each series' own % predicted is the backend *PercPred field the per-series
  // report pages render — passed through, never re-derived from best/predicted,
  // so the comparison cannot disagree with the rest of the report.
  const prePctPred = isFiniteNumber(pre[percPredKey]) ? pre[percPredKey] : undefined;
  const postPctPred = isFiniteNumber(post[percPredKey]) ? post[percPredKey] : undefined;

  if (!isFiniteNumber(preVal) || !isFiniteNumber(postVal)) {
    return {
      pre: isFiniteNumber(preVal) ? preVal : undefined,
      post: isFiniteNumber(postVal) ? postVal : undefined,
      predicted: isFiniteNumber(predicted) ? predicted : undefined,
      prePctPred,
      postPctPred,
      absChangeL: undefined,
      pctFromBaseline: undefined,
      pctFromPredicted: undefined,
      positiveResponse: undefined,
      positiveResponseByBasis: { baseline: undefined, predicted: undefined },
      significantDecline: undefined,
    };
  }

  const absChangeL = postVal - preVal;
  const pctFromBaseline = preVal !== 0 ? absChangeL / preVal : undefined;
  const pctFromPredicted = isFiniteNumber(predicted) && predicted !== 0
    ? absChangeL / predicted
    : undefined;

  // Both verdicts share the existing thresholds. The decision basis still
  // controls the legacy response and significant-decline highlighting.
  const isPredictedBasis = resolveBdBasis(thresholds.basis) === "predicted";
  const responsePct = isPredictedBasis ? pctFromPredicted : pctFromBaseline;
  const floorL = thresholds.absThresholdL;

  const meetsCriteria = (pct: number | undefined): boolean | undefined => pct !== undefined
    ? (floorL === undefined || absChangeL >= floorL - THRESHOLD_EPSILON)
    && pct >= thresholds.pctThreshold - THRESHOLD_EPSILON
    : undefined;
  const positiveResponseByBasis = {
    baseline: meetsCriteria(pctFromBaseline),
    predicted: meetsCriteria(pctFromPredicted),
  };
  const positiveResponse = positiveResponseByBasis[resolveBdBasis(thresholds.basis)];

  const significantDecline = responsePct !== undefined
    ? (floorL === undefined || absChangeL <= -floorL + THRESHOLD_EPSILON)
    && responsePct <= -thresholds.pctThreshold + THRESHOLD_EPSILON
    : undefined;

  return {
    pre: preVal,
    post: postVal,
    predicted: isFiniteNumber(predicted) ? predicted : undefined,
    prePctPred,
    postPctPred,
    absChangeL,
    pctFromBaseline,
    pctFromPredicted,
    positiveResponse,
    positiveResponseByBasis,
    significantDecline,
  };
}

export function computeBdComparison(
  pre: BdSeries,
  post: BdSeries,
  thresholds: BdThresholds = DEFAULT_BD_THRESHOLDS,
): BdComparison {
  return {
    fev1: compareMetric("fev1", pre, post, thresholds),
    fvc: compareMetric("fvc", pre, post, thresholds),
  };
}

export const bdInputValues = (series: FvcSeries | null | undefined, useAts2019?: boolean): BdSeries => {
  const best: any =
    useAts2019 && series?.qualityReport && (series.qualityReport as any).bestMeasurements
      ? (series.qualityReport as any).bestMeasurements
      : series;
  // A series with no acceptable effort (every effort rejected, or empty) has no "best"
  if (!(isFiniteNumber(best?.fev1) && best.fev1 > 0 && isFiniteNumber(best?.fvc) && best.fvc > 0)) {
    return {};
  }
  return {
    fev1: best?.fev1,
    fvc: best?.fvc,
    fev1Pred: best?.fev1Pred,
    fvcPred: best?.fvcPred,
    fev1PercPred: best?.fev1PercPred,
    fvcPercPred: best?.fvcPercPred,
  };
};

/**
 * Best-effort z-scores for the BD report's page-1 table and LLN chart. ATS-2019
 * reads them from `qualityReport.bestMeasurements`; otherwise from the series.
 * Absent values stay undefined so the report renders an em-dash, not "+0.0".
 */
export const extractBdZScores = (series: FvcSeries, useAts2019?: boolean): BdZScores => {
  const src: any =
    useAts2019 && series?.qualityReport?.bestMeasurements && "fvcZ" in (series.qualityReport.bestMeasurements as any)
      ? series.qualityReport.bestMeasurements
      : series;
  return {
    fvcZ: isFiniteNumber(src?.fvcZ) ? src.fvcZ : undefined,
    fev1Z: isFiniteNumber(src?.fev1Z) ? src.fev1Z : undefined,
    fev1FvcZ: isFiniteNumber(src?.fev1FvcZ) ? src.fev1FvcZ : undefined,
  };
};

export interface BdOrientationFields {
  seriesID?: SeriesID;
  created?: number;
}

/**
 * Assign pre/post roles to a linked pair.
 * The backend links partners with a single bidirectional `linkedBDSeriesID`:
 * the earlier `created` of the pair is the pre. Ties (and a defensively
 * missing `created`) fall back to seriesID ascending / viewed-as-pre,
 * mirroring the backend linker's (created, seriesID) sort so both sides
 * always agree on who the pre is.
 */
export function resolveBdOrientation<T extends BdOrientationFields>(
  viewed: T,
  companion: T,
): { pre: T; post: T } {
  let viewedIsPre: boolean;
  if (isFiniteNumber(viewed.created) && isFiniteNumber(companion.created) && viewed.created !== companion.created) {
    viewedIsPre = viewed.created < companion.created;
  } else {
    viewedIsPre = String(viewed.seriesID ?? "").localeCompare(String(companion.seriesID ?? "")) <= 0;
  }
  return viewedIsPre ? { pre: viewed, post: companion } : { pre: companion, post: viewed };
}

export type BdWaitOverride = "early" | "tooLong";

export interface BdAdministrationDisplay {
  administeredAtMs: number;
  dose?: string;
  waitOverride?: BdWaitOverride;
}

export const MAX_DOSE_DISPLAY_CHARS = 140;

/**
 * Read the kiosk-captured administration data off the post-BD series.
 */
export const resolveBdAdministration = (series: unknown): BdAdministrationDisplay | undefined => {
  const s = series as { bdAdministeredAt?: unknown; bdDose?: unknown; bdWaitOverride?: unknown } | null | undefined;
  const rawAt = s?.bdAdministeredAt;
  const administeredAtMs = typeof rawAt === "number" ? rawAt : typeof rawAt === "string" && rawAt.trim() !== "" ? Number(rawAt) : NaN;
  if (!Number.isFinite(administeredAtMs) || administeredAtMs <= 0) return undefined;
  const rawDose = s?.bdDose;
  const trimmedDose = typeof rawDose === "string" && rawDose.trim() !== "" ? rawDose.trim() : undefined;
  const dose = trimmedDose && trimmedDose.length > MAX_DOSE_DISPLAY_CHARS
    ? `${trimmedDose.slice(0, MAX_DOSE_DISPLAY_CHARS - 1)}…`
    : trimmedDose;
  const rawOverride = s?.bdWaitOverride;
  const waitOverride = rawOverride === "early" || rawOverride === "tooLong" ? rawOverride : undefined;
  return { administeredAtMs, dose, waitOverride };
};

/**
 * Display moment for the administered timestamp, anchored to the series' own
 * UTC offset (taken from its roundtripTime) so the clock time agrees with the
 * test times rendered beside it rather than the viewer's browser zone.
 */
export const bdAdministeredMoment = (administeredAtMs: number, seriesRoundtripTime?: string): Moment => {
  const m = moment(administeredAtMs);
  if (seriesRoundtripTime) {
    const anchor = moment.parseZone(seriesRoundtripTime);
    if (anchor.isValid()) m.utcOffset(anchor.utcOffset());
  }
  return m;
};

/**
 * Neutral clinical note for an overridden wait window — shared by the
 * comparison panel and the PDF report so the wording cannot drift.
 */
export const fmtBdWaitOverride = (waitOverride: BdWaitOverride | undefined): string | undefined =>
  waitOverride === "early"
    ? "Test started before the required post-administration wait elapsed (wait overridden for this visit)"
    : waitOverride === "tooLong"
      ? "Test started after the maximum post-administration window (provider override)"
      : undefined;

export const EM_DASH = "—";

export const fmtLiters = (v: number | undefined): string =>
  v === undefined ? EM_DASH : v.toFixed(2);

/**
 * Truncate `value` toward zero at `decimals` places, after stripping floating-point noise.
 */
const truncToward0 = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.trunc(Math.round(value * factor * 1e6) / 1e6) / factor;
};

export const fmtMl = (v: number | undefined): string =>
  v === undefined ? EM_DASH : `${truncToward0(v * 1000, 0)} mL`;

export const fmtPct = (v: number | undefined): string =>
  v === undefined ? EM_DASH : `${(v * 100).toFixed(2)}%`;

export const fmtPctChange = (v: number | undefined): string =>
  v === undefined ? EM_DASH : `${truncToward0(v * 100, 2).toFixed(2)}%`;

export const fmtReversibility = (v: boolean | undefined): string =>
  v === undefined ? EM_DASH : v ? "Yes" : "No";

/** Signed z-score to one decimal (e.g. "+1.2", "-0.4"); em-dash when absent. */
export const fmtZ = (v: number | undefined): string =>
  isFiniteNumber(v) ? `${v >= 0 ? "+" : ""}${v.toFixed(1)}` : EM_DASH;

export const fmtBdCriteria = (thresholds: BdThresholds = DEFAULT_BD_THRESHOLDS): string => {
  const pct = +(thresholds.pctThreshold * 100).toFixed(1);
  const pctTerm = resolveBdBasis(thresholds.basis) === "predicted" ? `>=${pct}% predicted` : `>=${pct}%`;
  if (thresholds.absThresholdL === undefined) return pctTerm;
  return `${pctTerm}, >=${Math.round(thresholds.absThresholdL * 1000)} mL`;
};

/**
 * Footer caption naming the reference the % change is measured against —
 * shared by the in-page panel and the PDF report.
 */
export const fmtBdBasisCaption = (thresholds: BdThresholds = DEFAULT_BD_THRESHOLDS): string =>
  resolveBdBasis(thresholds.basis) === "predicted"
    ? "% from Predicted = change relative to the predicted value"
    : "% from Baseline = change relative to the Pre-BD value";

/** Shared dashboard/PDF criteria for the visible verdicts; hidden verdicts retain the decision-reference caption. */
export const fmtBdDisplayCriteria = (settings: BdDisplaySettings): string => {
  const decisionBasis = resolveBdBasis(settings.thresholds.basis);
  const bases = settings.showVerdict ? settings.verdictBases : [decisionBasis];
  const criteria = bases.map(basis => {
    const thresholds = { ...settings.thresholds, basis };
    return `${fmtBdBasisCaption(thresholds)} · Positive response criteria: ${fmtBdCriteria(thresholds)}`;
  }).join(". ");
  return bases.length === 1 && bases[0] === decisionBasis
    ? criteria
    : `${criteria}. Significant decline highlighting uses ${BD_VERDICT_BASIS_LABELS[decisionBasis]}.`;
};
